import { Injectable, OnDestroy } from '@angular/core';
import { SwUpdate, VersionEvent, VersionReadyEvent } from '@angular/service-worker';
import { Subscription, filter } from 'rxjs';
import { AppMonitoring } from '../monitoring/monitor.app';

/**
 * Keeps the running app in sync with the deployed build.
 *
 * The service worker guarantees version consistency: once a version is cached it is served for
 * every navigation until a newer version has been fully downloaded *and activated*. Without any
 * activation handling the first load after a deployment runs the previous build — which renders
 * the wildcard 404 route for any path that build did not know about yet — and only a manual
 * refresh recovers. This service closes that gap.
 */
@Injectable({
  providedIn: 'root'
})
export class AppServiceWorkerUpdate implements OnDestroy {
  /** Session key recording the URL a stale-version recovery reload was already attempted for. */
  private readonly RECOVERY_ATTEMPTED_KEY = 'swStaleVersionRecoveryAttempted';
  /** Session key recording that an unrecoverable-state reload was already attempted. */
  private readonly UNRECOVERABLE_RELOAD_KEY = 'swUnrecoverableReloadAttempted';

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly swUpdate: SwUpdate,
    private readonly appMonitoring: AppMonitoring
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Starts listening for newly available versions. Call this once, from the root component.
   * When a new version becomes ready it is activated and the page is reloaded so the tab stops
   * running the superseded build.
   */
  registerForVersionUpdates(): void {
    if (!this.swUpdate.isEnabled) {
      this.appMonitoring.logEvent(this, 'Service worker is not enabled — version updates will not be tracked');
      return;
    }

    this.subscriptions.add(
      this.swUpdate.versionUpdates
        .pipe(filter((event: VersionEvent): event is VersionReadyEvent => event.type === 'VERSION_READY'))
        .subscribe((event: VersionReadyEvent) => {
          this.appMonitoring.logEvent(this, 'New app version is ready — activating and reloading', {
            currentVersion: event.currentVersion.hash,
            latestVersion: event.latestVersion.hash
          });
          this.activateAndReload();
        })
    );

    this.subscriptions.add(
      this.swUpdate.unrecoverable.subscribe((event) => {
        this.appMonitoring.logException(this, `Service worker is in an unrecoverable state: ${event.reason}`);
        // A single reload lets the service worker re-fetch its resources from the network.
        if (sessionStorage.getItem(this.UNRECOVERABLE_RELOAD_KEY)) {
          return;
        }
        sessionStorage.setItem(this.UNRECOVERABLE_RELOAD_KEY, 'true');
        this.reloadPage();
      })
    );
  }

  /**
   * Checks for a newer deployed version and reloads onto it. Intended for the wildcard 404 route:
   * a path the running build cannot match may simply be a path a newer build introduced.
   *
   * @param attemptedUrl The unmatched URL, used to guard against reloading the same URL twice.
   * @returns True when a reload was triggered — the caller should not treat the route as missing.
   */
  async recoverFromStaleVersion(attemptedUrl: string): Promise<boolean> {
    if (!this.swUpdate.isEnabled) {
      return false;
    }

    // Already reloaded once for this URL, so the route genuinely does not exist in the latest build.
    if (sessionStorage.getItem(this.RECOVERY_ATTEMPTED_KEY) === attemptedUrl) {
      this.appMonitoring.logEvent(this, 'Stale version recovery was already attempted for this URL', {
        attemptedUrl
      });
      return false;
    }

    try {
      const isNewVersionAvailable = await this.swUpdate.checkForUpdate();
      if (!isNewVersionAvailable) {
        return false;
      }

      const isActivated = await this.swUpdate.activateUpdate();
      if (!isActivated) {
        this.appMonitoring.logEvent(this, 'A new version was found but could not be activated', { attemptedUrl });
        return false;
      }

      this.appMonitoring.logEvent(this, 'Unmatched URL resolved by a newer app version — reloading', {
        attemptedUrl
      });
      sessionStorage.setItem(this.RECOVERY_ATTEMPTED_KEY, attemptedUrl);
      this.reloadPage();
      return true;
    } catch (error: unknown) {
      this.appMonitoring.logException(
        this,
        error instanceof Error ? error : 'Failed to check for a newer app version',
        { attemptedUrl }
      );
      return false;
    }
  }

  private activateAndReload(): void {
    this.swUpdate
      .activateUpdate()
      .then((isActivated: boolean) => {
        if (isActivated) {
          this.reloadPage();
        } else {
          this.appMonitoring.logEvent(this, 'New app version could not be activated');
        }
      })
      .catch((error: unknown) => {
        this.appMonitoring.logException(
          this,
          error instanceof Error ? error : 'Failed to activate the new app version'
        );
      });
  }

  /** Wrapped so that reloading can be verified in unit tests. */
  private reloadPage(): void {
    document.location.reload();
  }
}
